import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ASSETS, CONTACT_EMAILS, SOCIAL, HANDLE } from "@/lib/brand";
import SocialIcon from "@/components/SocialIcon";
import Seo from "@/components/Seo";

const INTERESTS = [
  { value: "press", label: "Press · Media" },
  { value: "speaking", label: "Speaking · Appearance" },
  { value: "partnership", label: "Brand Partnership" },
  { value: "philanthropy", label: "Better Tomorrow · Philanthropy" },
  { value: "general", label: "General Inquiry" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", subject: "", message: "", interest: "general",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", message: "" });
    try {
      await api.post("/contact", form);
      setStatus({ state: "success", message: "Thank you. Your inquiry has reached the Office of Miguel Santana. Expect a reply within three business days." });
      setForm({ name: "", email: "", company: "", subject: "", message: "", interest: "general" });
    } catch (err) {
      setStatus({ state: "error", message: formatApiError(err) });
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-20 px-6 md:px-12">
      <Seo
        title="Contact · Book / Inquire"
        description="Direct inquiries to the Office of Miguel Santana — press, podcast bookings, brand partnerships, philanthropic collaborations, and speaking engagements."
        path="/contact"
      />
      <div className="max-w-[1440px] mx-auto grid grid-cols-12 gap-8 md:gap-16">
        {/* Left editorial */}
        <div className="col-span-12 lg:col-span-5">
          <p className="font-overline text-champagne mb-6">— Contact</p>
          <h1 className="font-display text-5xl md:text-7xl text-silver leading-[0.95] tracking-tighter">
            Direct<br />
            <span className="italic font-light">Inquiries.</span>
          </h1>
          <p className="mt-10 font-body text-silver-muted text-lg leading-relaxed max-w-md">
            For press, speaking, brand partnerships, philanthropic collaborations, and bookings.
            All inquiries are reviewed by the Office of Miguel Santana.
          </p>

          <div className="mt-16 space-y-8" data-testid="contact-emails-block">
            <div>
              <p className="font-overline text-champagne mb-3">— Direct Lines</p>
              <ul className="space-y-4">
                {CONTACT_EMAILS.map((c) => (
                  <li key={c.value} className="border-b hairline pb-3">
                    <p className="font-overline text-silver-muted">{c.label}</p>
                    <a
                      href={`mailto:${c.value}`}
                      data-testid={`contact-email-${c.label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                      className="font-display text-xl text-silver hover:text-champagne transition-colors block mt-1"
                    >
                      {c.value}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="font-overline text-champagne mb-3">— Follow {HANDLE}</p>
              <div className="flex flex-wrap gap-3">
                {SOCIAL.map((s) => (
                  <SocialIcon
                    key={s.name}
                    icon={s.icon}
                    label={s.name}
                    href={s.url}
                    testId={`contact-social-${s.icon}`}
                  />
                ))}
              </div>
            </div>

            <div>
              <p className="font-overline text-silver-muted">Press Cycle</p>
              <p className="mt-2 font-display text-2xl text-silver">3 business days</p>
            </div>
            <div>
              <p className="font-overline text-silver-muted">Headquartered</p>
              <p className="mt-2 font-display text-2xl text-silver">New York</p>
            </div>
          </div>

          <img
            src={ASSETS.signature}
            alt="Signature"
            className="signature-img mt-16 w-56 h-auto"
          />
        </div>

        {/* Right form */}
        <div className="col-span-12 lg:col-span-7 lg:pl-12 lg:border-l hairline">
          <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
              <div>
                <label className="font-overline text-silver-muted block mb-1">Full Name</label>
                <input
                  required
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={handle("name")}
                  className="input-editorial"
                  placeholder="Jane Carter"
                />
              </div>
              <div>
                <label className="font-overline text-silver-muted block mb-1">Email</label>
                <input
                  required
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={handle("email")}
                  className="input-editorial"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="font-overline text-silver-muted block mb-1">Company · Outlet</label>
                <input
                  data-testid="contact-input-company"
                  value={form.company}
                  onChange={handle("company")}
                  className="input-editorial"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="font-overline text-silver-muted block mb-1">Subject</label>
                <input
                  data-testid="contact-input-subject"
                  value={form.subject}
                  onChange={handle("subject")}
                  className="input-editorial"
                  placeholder="Subject of your inquiry"
                />
              </div>
            </div>

            <div>
              <label className="font-overline text-silver-muted block mb-3">Nature of Inquiry</label>
              <div className="flex flex-wrap gap-3">
                {INTERESTS.map((opt) => {
                  const active = form.interest === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`contact-interest-${opt.value}`}
                      onClick={() => setForm((f) => ({ ...f, interest: opt.value }))}
                      className={`px-4 py-2 border text-xs uppercase tracking-[0.2em] transition-all duration-300 ${
                        active
                          ? "bg-silver text-[#050A15] border-silver"
                          : "border-white/20 text-silver-muted hover:text-silver hover:border-white/40"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="font-overline text-silver-muted block mb-1">Message</label>
              <textarea
                required
                rows={6}
                data-testid="contact-input-message"
                value={form.message}
                onChange={handle("message")}
                className="input-editorial"
                placeholder="Tell us about your inquiry in detail."
              />
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-6 border-t hairline">
              <p className="font-overline text-silver-muted max-w-md">
                Submitting forwards your inquiry to the Office of Miguel Santana.
              </p>
              <button
                type="submit"
                disabled={status.state === "submitting"}
                data-testid="contact-submit"
                className="btn-editorial btn-editorial-filled disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status.state === "submitting" ? "Sending…" : "Send Inquiry"}
              </button>
            </div>

            {status.state === "success" && (
              <div data-testid="contact-success" className="border hairline-strong p-6">
                <p className="font-overline text-champagne">Inquiry Received</p>
                <p className="mt-3 font-body text-silver">{status.message}</p>
              </div>
            )}
            {status.state === "error" && (
              <div data-testid="contact-error" className="border border-red-900 p-6">
                <p className="font-overline text-red-300">Error</p>
                <p className="mt-3 font-body text-silver-muted">{status.message}</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
